export function PrivacyContent() {
  return (
    <div className="prose prose-lg max-w-4xl w-full">
      <h1>Privacy Policy</h1>
      <p className="text-sm text-muted">Last Updated: November 17, 2025</p>

      <h2>Introduction</h2>
      <p>
        Welcome to 206mp (206 Megapixels). We are committed to protecting your privacy and being
        transparent about how we collect, use, and safeguard your information. This Privacy Policy
        explains how we handle your data when you use our photo critique and collection platform.
      </p>

      <h2>Information We Collect</h2>

      <h3>Account Information</h3>
      <p>When you create an account, we collect:</p>
      <ul>
        <li>Email address (required for account verification and communication)</li>
        <li>Username and display name</li>
        <li>Password (encrypted and never stored in plain text)</li>
        <li>Profile information you choose to provide</li>
      </ul>

      <h3>Photos and Content</h3>
      <p>
        When you upload photos to 206mp, we collect and store your images along with associated
        metadata including:
      </p>
      <ul>
        <li>EXIF data (camera settings, timestamps, equipment information)</li>
        <li>Photo titles, descriptions, and captions you provide</li>
        <li>Critiques and comments you create</li>
        <li>Collections and organizational data</li>
      </ul>
      <p>
        <strong>Important:</strong> You retain full copyright ownership of all photos you upload.
        You can delete your photos at any time, and we will permanently remove them from our
        servers and cloud storage providers with no copies maintained.
      </p>

      <h3>Usage Analytics</h3>
      <p>To improve our platform, we collect:</p>
      <ul>
        <li>Usage patterns and interaction data</li>
        <li>Page views and navigation patterns</li>
        <li>Feature usage statistics</li>
        <li>Performance and error logs</li>
      </ul>

      <h3>Technical Information</h3>
      <p>We automatically collect certain technical data:</p>
      <ul>
        <li>Browser type and version</li>
        <li>Operating system</li>
        <li>IP address and approximate geographic location</li>
        <li>Device identifiers</li>
        <li>Referral URLs</li>
      </ul>

      <h3>Third-Party Authentication</h3>
      <p>
        If you choose to authenticate using third-party services (such as Google or other OAuth
        providers), we may receive basic profile information from those services in accordance
        with their privacy policies and your consent.
      </p>

      <h2>How We Use Your Information</h2>
      <p>We use your information solely to:</p>
      <ul>
        <li>Provide and maintain the 206mp platform functionality</li>
        <li>Enable photo critique and collection features</li>
        <li>Send transactional emails (verification, notifications, account updates)</li>
        <li>Improve our services through analytics and usage insights</li>
        <li>Ensure platform security and prevent abuse</li>
        <li>Respond to your support requests and communications</li>
      </ul>
      <p>
        We do <strong>NOT</strong> use your photos for marketing, promotional materials, machine
        learning training, or any purpose other than providing platform functionality, unless you
        explicitly grant permission for specific use cases.
      </p>

      <h2>Data Sharing and Third Parties</h2>
      <p>We share your data only with trusted service providers necessary to operate our platform:</p>

      <h3>Cloud Storage and Hosting</h3>
      <p>
        Your photos are stored securely with cloud infrastructure providers (such as AWS S3,
        Railway, or similar services). These providers are bound by strict data processing
        agreements and security standards.
      </p>

      <h3>Email Service Providers</h3>
      <p>
        We use third-party email services to send transactional emails such as account
        verification, password resets, and important notifications.
      </p>

      <h3>Analytics Services</h3>
      <p>
        We may use analytics services to understand how users interact with our platform. These
        services collect anonymized usage data to help us improve the user experience.
      </p>

      <h3>No Sale of Data</h3>
      <p>
        We do <strong>NOT</strong> sell, rent, or trade your personal information or photos to
        third parties for their marketing purposes.
      </p>

      <h2>Data Security</h2>
      <p>We implement industry-standard security measures to protect your data:</p>
      <ul>
        <li>Encrypted password storage using bcrypt</li>
        <li>Secure HTTPS connections for all data transmission</li>
        <li>Regular security audits and updates</li>
        <li>Access controls and authentication mechanisms</li>
        <li>Secure cloud storage with encryption at rest</li>
      </ul>

      <h2>Your Rights and Controls</h2>
      <p>You have the following rights regarding your data:</p>
      <ul>
        <li>
          <strong>Access:</strong> You can view and download your photos and account information at
          any time
        </li>
        <li>
          <strong>Deletion:</strong> You can permanently delete your photos with immediate removal
          from our systems
        </li>
        <li>
          <strong>Account Closure:</strong> You can request full account deletion by contacting us
        </li>
        <li>
          <strong>Data Portability:</strong> You can export your photos and data
        </li>
        <li>
          <strong>Opt-Out:</strong> You can opt out of non-essential communications
        </li>
      </ul>

      <h2>Data Retention</h2>
      <p>
        We retain your account information and photos for as long as your account is active. When
        you delete photos, they are immediately and permanently removed from our servers and cloud
        storage with no backup copies maintained. If you close your account, we will delete your
        personal information within 30 days, except as required by law or for legitimate business
        purposes (such as resolving disputes or enforcing agreements).
      </p>

      <h2>Cookies and Tracking</h2>
      <p>
        We use essential cookies to maintain your login session and remember your preferences. We
        may also use analytics cookies to understand platform usage. You can control cookie
        settings through your browser, though disabling essential cookies may limit platform
        functionality.
      </p>

      <h2>Children's Privacy</h2>
      <p>
        206mp is not intended for users under the age of 13. We do not knowingly collect personal
        information from children. If you believe a child has provided us with personal
        information, please contact us immediately.
      </p>

      <h2>International Users</h2>
      <p>
        Our servers are located in the United States. If you access 206mp from outside the U.S.,
        your information will be transferred to and processed in the United States. By using our
        platform, you consent to this transfer.
      </p>

      <h2>Changes to This Policy</h2>
      <p>
        We may update this Privacy Policy from time to time. We will notify you of significant
        changes by email or through a prominent notice on our platform. Your continued use of 206mp
        after changes take effect constitutes acceptance of the updated policy.
      </p>

      <h2>Monetization and Advertising</h2>
      <p>
        Our monetization strategy is currently under development. We are exploring options including
        subscription-based premium features. If we introduce advertising in the future, we will
        update this policy and provide clear notice to users. We are committed to maintaining a
        high-quality user experience regardless of our monetization approach.
      </p>

      <h2>Contact Us</h2>
      <p>
        If you have questions, concerns, or requests regarding this Privacy Policy or your data,
        please contact us at:
      </p>
      <p className="ml-4">
        <strong>Email:</strong> admin@206mp.com
      </p>
      <p>We will respond to your inquiry within 30 days.</p>

      <hr className="my-8" />

      <p className="text-sm text-muted">
        This privacy policy is effective as of November 17, 2025, and applies to all users of the
        206mp platform.
      </p>
    </div>
  );
}
